/**
 * Recommendations List Component
 *
 * Displays prioritized recommendations for improving visibility.
 */

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown, ChevronRight, Lightbulb, AlertTriangle, ArrowUp, Minus, Code, ExternalLink } from 'lucide-react';
import { useState } from 'react';
import { Recommendation } from '@/services/visibilityDiagnosisService';

interface RecommendationsListProps {
  recommendations: Recommendation[];
}

const priorityConfig = {
  critical: {
    icon: AlertTriangle,
    color: 'text-red-600 dark:text-red-400',
    bgColor: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800',
    badge: 'destructive' as const,
  },
  high: {
    icon: ArrowUp,
    color: 'text-orange-600 dark:text-orange-400',
    bgColor: 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800',
    badge: 'default' as const,
  },
  medium: {
    icon: Minus,
    color: 'text-yellow-600 dark:text-yellow-400',
    bgColor: 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800',
    badge: 'secondary' as const,
  },
  low: {
    icon: Lightbulb,
    color: 'text-blue-600 dark:text-blue-400',
    bgColor: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800',
    badge: 'outline' as const,
  },
};

const categoryLabels: Record<string, string> = {
  robots_txt: 'Robots.txt',
  structured_data: 'Structured Data',
  internal_linking: 'Internal Linking',
  content: 'Content',
  outreach: 'Outreach',
  technical: 'Technical',
};

export function RecommendationsList({ recommendations }: RecommendationsListProps) {
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set([0])); // First item expanded by default

  const toggleItem = (index: number) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedItems(newExpanded);
  };

  if (recommendations.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lightbulb className="h-5 w-5" />
            Recommendations
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">No recommendations available. Run a diagnosis to get actionable suggestions.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Lightbulb className="h-5 w-5 text-yellow-500" />
          Recommendations
          <Badge variant="outline" className="ml-2">{recommendations.length}</Badge>
        </CardTitle>
        <CardDescription>
          Prioritized actions to improve your AI and search engine visibility
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {recommendations.map((rec, index) => {
          const config = priorityConfig[rec.priority] || priorityConfig.medium;
          const Icon = config.icon;
          const isExpanded = expandedItems.has(index);

          return (
            <Collapsible key={index} open={isExpanded} onOpenChange={() => toggleItem(index)}>
              <div className={`border rounded-lg ${config.bgColor} overflow-hidden`}>
                <CollapsibleTrigger asChild>
                  <button className="w-full p-4 flex items-start gap-3 text-left hover:bg-black/5 transition-colors">
                    <Icon className={`h-5 w-5 mt-0.5 ${config.color}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant={config.badge} className="text-xs">
                          {rec.priority.charAt(0).toUpperCase() + rec.priority.slice(1)}
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          {categoryLabels[rec.category] || rec.category}
                        </Badge>
                      </div>
                      <h4 className="font-medium mt-1">{rec.title}</h4>
                      <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{rec.description}</p>
                    </div>
                    {isExpanded ? (
                      <ChevronDown className="h-5 w-5 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="h-5 w-5 text-muted-foreground" />
                    )}
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="px-4 pb-4 pt-0 border-t bg-white/50 dark:bg-black/20">
                    <div className="space-y-4 pt-4">
                      {rec.implementation && (
                        <div>
                          <h5 className="text-sm font-medium flex items-center gap-2 mb-2">
                            <Code className="h-4 w-4" />
                            Implementation
                          </h5>
                          <pre className="text-xs bg-slate-900 text-slate-100 p-3 rounded-md overflow-x-auto whitespace-pre-wrap">
                            {rec.implementation}
                          </pre>
                        </div>
                      )}
                      <div>
                        <h5 className="text-sm font-medium mb-1">Expected Impact</h5>
                        <p className="text-sm text-muted-foreground">{rec.impact}</p>
                      </div>
                    </div>
                  </div>
                </CollapsibleContent>
              </div>
            </Collapsible>
          );
        })}
      </CardContent>
    </Card>
  );
}
